import type { CSSProperties, JSX, KeyboardEvent, ReactNode } from "react";
import { cx } from "../cx";

export interface FeedColumn {
  id: string;
  /** Caps column header; omit for icon/utility columns. */
  label?: ReactNode | undefined;
  /** number = px; string = CSS track ("1fr", "minmax(…)"). Default "1fr". */
  width?: number | string | undefined;
  align?: "left" | "right" | "center" | undefined;
  /** Render the column's cells in IBM Plex Mono at 12px. */
  mono?: boolean | undefined;
}

export interface FeedRow {
  id: string;
  /** Cell content keyed by column id. */
  cells: Readonly<Record<string, ReactNode>>;
  /** Selected row — blue-tinted surface + 2px selection inset. */
  selected?: boolean | undefined;
  /** Makes the row focusable; fires on click, Enter, and Space. */
  onSelect?: (() => void) | undefined;
  /** Consecutive rows sharing a groupKey render under one group header. */
  groupKey?: string | undefined;
}

export interface FeedTableProps {
  columns?: ReadonlyArray<FeedColumn> | undefined;
  rows?: ReadonlyArray<FeedRow> | undefined;
  /** Accessible name for the grid. */
  label?: string | undefined;
  /** Empty slot — rendered instead of rows (e.g. an EmptyState). */
  children?: ReactNode | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
}

type FeedGroupSegment = { kind: "group"; key: string; count: number; firstRowId: string };
type FeedSegment = FeedGroupSegment | { kind: "row"; row: FeedRow };

function segment(rows: ReadonlyArray<FeedRow>): FeedSegment[] {
  const segments: FeedSegment[] = [];
  let currentGroup: FeedGroupSegment | undefined;
  for (const row of rows) {
    if (row.groupKey === undefined) {
      currentGroup = undefined;
    } else if (currentGroup === undefined || currentGroup.key !== row.groupKey) {
      currentGroup = { kind: "group", key: row.groupKey, count: 0, firstRowId: row.id };
      segments.push(currentGroup);
    }
    if (currentGroup !== undefined) {
      currentGroup.count += 1;
    }
    segments.push({ kind: "row", row });
  }
  return segments;
}

/**
 * Density-aware data feed for high-volume studio views. Semantically a
 * native table; visually CSS-grid rows with a caps header and hairline
 * separators — row height tracks --row-h and cell padding tracks
 * --cell-pad-x. Selectable rows are focusable and activate on click, Enter,
 * or Space; the selected row is exposed through aria-current and gets the
 * blue-tinted surface with the sanctioned 2px selection inset. Consecutive
 * rows sharing a groupKey gain a caps group header with a count. Ported from
 * design/tarski-kit/components/studio/FeedTable.jsx.
 */
export function FeedTable({
  columns = [],
  rows = [],
  label,
  children,
  className,
  style,
}: FeedTableProps): JSX.Element {
  const template = columns
    .map((column) =>
      typeof column.width === "number" ? `${column.width}px` : (column.width ?? "1fr"),
    )
    .join(" ");
  const rootStyle: CSSProperties & { "--tk-feed-columns"?: string } = {
    "--tk-feed-columns": template,
    ...style,
  };
  const alignClass = (column: FeedColumn) =>
    column.align === "right" || column.align === "center"
      ? `tk-feed-table__cell--${column.align}`
      : undefined;

  const handleRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, row: FeedRow) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    if (event.target !== event.currentTarget) {
      return;
    }
    event.preventDefault();
    row.onSelect?.();
  };

  return (
    <div className={cx("tk-feed-table", className)} style={rootStyle}>
      <table aria-label={label} className="tk-feed-table__grid">
        <thead className="tk-feed-table__head">
          <tr className="tk-feed-table__header">
            {columns.map((column) => (
              <th
                key={column.id}
                scope="col"
                className={cx("tk-feed-table__columnheader", alignClass(column))}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="tk-feed-table__body">
          {segment(rows).map((item) =>
            item.kind === "group" ? (
              <tr key={`group-${item.firstRowId}`} className="tk-feed-table__group-row">
                <td colSpan={columns.length} className="tk-feed-table__group">
                  <span>{item.key}</span>{" "}
                  <span className="tk-feed-table__group-count">{item.count}</span>
                </td>
              </tr>
            ) : (
              <tr
                key={item.row.id}
                aria-current={item.row.selected === true ? "true" : undefined}
                tabIndex={item.row.onSelect === undefined ? undefined : 0}
                className={cx(
                  "tk-feed-table__row",
                  item.row.onSelect !== undefined && "tk-feed-table__row--selectable",
                  item.row.selected === true && "tk-feed-table__row--selected",
                )}
                onClick={item.row.onSelect}
                onKeyDown={(event) => handleRowKeyDown(event, item.row)}
              >
                {columns.map((column) => (
                  <td
                    key={column.id}
                    className={cx(
                      "tk-feed-table__cell",
                      column.mono === true && "tk-feed-table__cell--mono",
                      alignClass(column),
                    )}
                  >
                    {item.row.cells[column.id]}
                  </td>
                ))}
              </tr>
            ),
          )}
        </tbody>
      </table>
      {rows.length === 0 && children !== undefined ? (
        <div className="tk-feed-table__empty">{children}</div>
      ) : null}
    </div>
  );
}
